import React, { useCallback, useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import {
  isPushSupported,
  getPushPermissionState,
  requestPushPermissionAndRegister,
} from '../../../services/pushNotificationService';
import { hapticNotification } from '../../../native/haptics';
import './PushNotificationPrompt.css';

const DISMISSED_UNTIL_KEY = 'push_prompt_dismissed_until';
const DISMISS_DELAY_MS = 7 * 24 * 60 * 60 * 1000;

type PromptState = 'checking' | 'hidden' | 'prompt' | 'denied';

const isDismissedForNow = (): boolean => {
  const until = Number(localStorage.getItem(DISMISSED_UNTIL_KEY) || 0);
  return until > Date.now();
};

/**
 * Bannière contextuelle d'activation des notifications push — app native uniquement.
 * Ne rend jamais rien sur le web (garde `isPushSupported`).
 */
const PushNotificationPrompt: React.FC = () => {
  const { t } = useTranslation('native');
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<PromptState>('checking');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isPushSupported() || !isAuthenticated || isDismissedForNow()) {
      setState('hidden');
      return;
    }
    const perm = await getPushPermissionState();
    if (perm === 'granted') {
      setState('hidden');
      return;
    }
    setState(perm === 'denied' ? 'hidden' : 'prompt');
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const result = await requestPushPermissionAndRegister();
      if (result.ok) {
        localStorage.removeItem(DISMISSED_UNTIL_KEY);
        void hapticNotification();
        void import('../../../native/nativeAnalytics').then((m) => m.trackPushPermissionGranted());
        toast.success(t('settings.pushEnabled'));
        setState('hidden');
      } else if (result.reason === 'denied') {
        void import('../../../native/nativeAnalytics').then((m) => m.trackPushPermissionDenied());
        toast.info(t('settings.pushEnableInSettings'));
        setState('hidden');
      } else {
        toast.error(t('settings.pushEnableFailed'));
        await refresh();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_UNTIL_KEY, String(Date.now() + DISMISS_DELAY_MS));
    setState('hidden');
  };

  if (state !== 'prompt') return null;

  return (
    <div className="push-prompt" role="region" aria-label={t("pushPrompt.aria")}>
      <div className="push-prompt__icon">
        <Bell size={20} />
      </div>
      <div className="push-prompt__text">
        <span className="push-prompt__title">{t("pushPrompt.title")}</span>
        <span className="push-prompt__desc">{t("pushPrompt.desc")}</span>
      </div>
      <div className="push-prompt__actions">
        <button
          type="button"
          className="push-prompt__btn"
          onClick={handleActivate}
          disabled={loading}
        >
          {loading ? "…" : t("pushPrompt.activate")}
        </button>
        <button
          type="button"
          className="push-prompt__close"
          onClick={handleDismiss}
          aria-label={t("pushPrompt.dismissAria")}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
